export type WindowType = 'traversing_drape' | 'side_panel' | 'roman_shade' | 'roller_shade'
  | 'roman_valance' | 'pleated_valance' | 'tailored_valance' | 'shirred_valance' | 'balloon_shade' | 'austrian_shade';

export type SoftGoodsType = 'pillow' | 'sham' | 'bolster_pillow' | 'cushion'
  | 'dustskirt' | 'box_spring_cover' | 'duvet' | 'coverlet'
  | 'tablecloth_round' | 'tablecloth_rectangle';

export type FurnitureType = 'furniture';

export type BenchType = 'bench';

export type CorniceType = 'cornice';

export type BedType = 'bed';

export type ReupholsterBenchType = 'reupholster_bench';

export type ReupholsterOttomanType = 'reupholster_ottoman';

export type ReupholsterPopSeatType = 'reupholster_pop_seat';

export type ReupholsterDiningChairType = 'reupholster_dining_chair';

export type PinBoardType = 'pin_board';

export type ItemTypeEnum = WindowType | SoftGoodsType | FurnitureType | BenchType | CorniceType | BedType | ReupholsterBenchType | ReupholsterOttomanType | ReupholsterPopSeatType | ReupholsterDiningChairType | PinBoardType;

export const WINDOW_TYPES: { value: WindowType; label: string }[] = [
  { value: 'traversing_drape',  label: 'Traversing Drape' },
  { value: 'side_panel',        label: 'Side Panel' },
  { value: 'roman_shade',       label: 'Roman Shade' },
  { value: 'roller_shade',      label: 'Roller Shade' },
  { value: 'roman_valance',     label: 'Roman Valance' },
  { value: 'pleated_valance',   label: 'Pleated Valance' },
  { value: 'tailored_valance',  label: 'Tailored Valance' },
  { value: 'shirred_valance',   label: 'Shirred Valance' },
  { value: 'balloon_shade',     label: 'Balloon Shade' },
  { value: 'austrian_shade',    label: 'Austrian Shade' },
];

export const SOFT_GOODS_TYPES: { value: SoftGoodsType; label: string }[] = [
  { value: 'pillow',              label: 'Pillow' },
  { value: 'sham',                label: 'Sham' },
  { value: 'bolster_pillow',      label: 'Bolster Pillow' },
  { value: 'cushion',             label: 'Cushion' },
  { value: 'dustskirt',           label: 'Dustskirt' },
  { value: 'box_spring_cover',    label: 'Box Spring Cover' },
  { value: 'duvet',                label: 'Duvet' },
  { value: 'coverlet',            label: 'Coverlet' },
  { value: 'tablecloth_round',    label: 'Tablecloth (Round)' },
  { value: 'tablecloth_rectangle', label: 'Tablecloth (Rectangle)' },
];

// No SOP exists yet for furniture pricing — added purely to test the
// quote → work order flow end to end. See calcFurniture() in
// server/src/plugins/sopCalculations.ts.
export const FURNITURE_TYPES: { value: FurnitureType; label: string }[] = [
  { value: 'furniture', label: 'Furniture (Sofa/Loveseat — best guess pricing)' },
];

// Custom Benches — see Quoting_markdown/benches.md / benchCalculations.ts.
export const BENCH_TYPES: { value: BenchType; label: string }[] = [
  { value: 'bench', label: 'Bench' },
];

// Custom Cornices — see Quoting_markdown/cornices.md / corniceCalculations.ts.
export const CORNICE_TYPES: { value: CorniceType; label: string }[] = [
  { value: 'cornice', label: 'Cornice' },
];

// Custom Beds — see Quoting_markdown/beds.md / bedCalculations.ts.
export const BED_TYPES: { value: BedType; label: string }[] = [
  { value: 'bed', label: 'Bed' },
];

// Reupholster Benches — a genuinely separate service from New Custom Benches
// above, see Quoting_markdown/reupholster_benches.md / reupholsterBenchCalculations.ts.
export const REUPHOLSTER_BENCH_TYPES: { value: ReupholsterBenchType; label: string }[] = [
  { value: 'reupholster_bench', label: 'Reupholster Bench' },
];

// Reupholster Ottomans — shares most of Reupholster Bench's option set and
// fabric-yardage cuts, but priced per square foot of the top rather than per
// inch of width. See Quoting_markdown/reupholster_ottomans.md /
// reupholsterOttomanCalculations.ts.
export const REUPHOLSTER_OTTOMAN_TYPES: { value: ReupholsterOttomanType; label: string }[] = [
  { value: 'reupholster_ottoman', label: 'Reupholster Ottoman' },
];

// Reupholster Pop Seats — small drop-in seat cushions, fully self-contained
// (no back, no skirt, no delivery charge). See
// Quoting_markdown/reupholster_dining_chairs.md / reupholsterPopSeatCalculations.ts.
export const REUPHOLSTER_POP_SEAT_TYPES: { value: ReupholsterPopSeatType; label: string }[] = [
  { value: 'reupholster_pop_seat', label: 'Reupholster Pop Seat' },
];

// Reupholster Dining Chairs — seat plus optional inside/outside back, each
// independently finished, plus shared whole-chair add-ons. See
// Quoting_markdown/reupholster_dining_chairs.md / reupholsterDiningChairCalculations.ts.
export const REUPHOLSTER_DINING_CHAIR_TYPES: { value: ReupholsterDiningChairType; label: string }[] = [
  { value: 'reupholster_dining_chair', label: 'Reupholster Dining Chair' },
];

// Custom Pin Boards — see Quoting_markdown/pin_boards.md / pinBoardCalculations.ts.
export const PIN_BOARD_TYPES: { value: PinBoardType; label: string }[] = [
  { value: 'pin_board', label: 'Pin Board' },
];

export const ALL_TYPES = [...WINDOW_TYPES, ...SOFT_GOODS_TYPES, ...FURNITURE_TYPES, ...BENCH_TYPES, ...CORNICE_TYPES, ...BED_TYPES, ...REUPHOLSTER_BENCH_TYPES, ...REUPHOLSTER_OTTOMAN_TYPES, ...REUPHOLSTER_POP_SEAT_TYPES, ...REUPHOLSTER_DINING_CHAIR_TYPES, ...PIN_BOARD_TYPES];

// Contract every per-product-type form component implements. Each type owns its
// own internal question set/state; the parent only knows how to ask it for the
// final values (or null if the component's own validation failed).
export interface ObjectTypeFormHandle {
  getValues: () => Record<string, any> | null;
}

export interface ObjectTypeFormProps {
  disabled: boolean;
  // When set, the form should hydrate its fields from these values instead of
  // starting blank — used by the "Go back" flow to let a user re-edit the
  // inputs behind an already-saved object and recompute it.
  initialValues?: Record<string, any> | null;
}
